import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { contractAPI, propertyAPI } from '../../services/api';
import './PropertySelect.css';

const PropertySelect = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = location.state?.returnUrl || '/contract/create';
  const contractId = location.state?.contractId;
  const contractType = location.state?.contractType; // Preserve contract type

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({
    realEstateUsageId: '',
    realEstateTypeId: '',
    ownerDocumentType: '',
    propertyName: ''
  });

  const [dropdowns, setDropdowns] = useState({
    realEstateUsages: [],
    realEstateTypes: []
  });

  useEffect(() => {
    loadDropdowns();
    // Load all properties on initial mount (matching MVC behavior)
    loadProperties();
  }, []);

  useEffect(() => {
    // Reload when page changes
    loadProperties();
  }, [page]);

  const loadDropdowns = async () => {
    try {
      const response = await propertyAPI.getDropdowns();
      const data = response.data || {};
      setDropdowns({
        realEstateUsages: data.RealEstateUsages || data.realEstateUsages || [],
        realEstateTypes: data.RealEstateTypes || data.realEstateTypes || []
      });
    } catch (err) {
      console.error('Failed to load dropdowns:', err);
    }
  };

  const loadProperties = async () => {
    try {
      setLoading(true);

      // Build filter parameters - send null for empty filters (to get all properties)
      const filterParams = {
        realEstateUsageId: filters.realEstateUsageId ? parseInt(filters.realEstateUsageId) : null,
        realEstateTypeId: filters.realEstateTypeId ? parseInt(filters.realEstateTypeId) : null,
        ownerDocumentType: filters.ownerDocumentType ? parseInt(filters.ownerDocumentType) : null,
        propertyName: filters.propertyName && filters.propertyName.trim() ? filters.propertyName.trim() : null,
        page,
        pageSize
      };

      const response = await contractAPI.getPropertiesWithDetails(filterParams);

      const data = response.data || response || {};

      // Handle both array and object responses
      let propertiesList = [];
      if (Array.isArray(data)) {
        propertiesList = data;
      } else if (data.properties) {
        propertiesList = data.properties;
      } else if (data.items) {
        propertiesList = data.items;
      }

      setProperties(propertiesList);
      setTotalCount(data.totalCount || propertiesList.length);
      setTotalPages(Math.ceil((data.totalCount || propertiesList.length) / pageSize));
    } catch (err) {
      console.error('Failed to load properties:', err);
      console.error('Error details:', err.response?.data || err.message);
      setProperties([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    setPage(1);
    loadProperties();
  };

  const handleClearFilter = () => {
    setFilters({
      realEstateUsageId: '',
      realEstateTypeId: '',
      ownerDocumentType: '',
      propertyName: ''
    });
    setPage(1);
    // Reload properties after clearing filters
    setTimeout(() => {
      loadProperties();
    }, 100);
  };

  const handleSelectProperty = (propertyId) => {
    setSelectedPropertyId(propertyId);
  };

  const handleConfirm = () => {
    if (!selectedPropertyId) {
      alert(t('property.select.list.selectPropertyFirst'));
      return;
    }

    // Store in sessionStorage and navigate back
    sessionStorage.setItem('selectedPropertyId', selectedPropertyId.toString());
    sessionStorage.setItem('returnToTab', '1');

    // Also store contractId if provided
    if (contractId) {
      sessionStorage.setItem('contractId', contractId.toString());
    }

    navigate(returnUrl);
  };

  return (
    <div className="property-select-container">
      <div className="content-card">
        {/* Property Filter Section */}
        <div className="card mb-4">
          <div className="card-header">
            <h5>{t('property.select.filter.title')}</h5>
          </div>
          <div className="card-body">
            <div className="filter-grid">
              <div className="filter-item">
                <label className="form-label">
                  <i className="fas fa-home"></i> {t('property.select.filter.propertyUsage')}
                </label>
                <select
                  className="form-select filter-select"
                  value={filters.realEstateUsageId}
                  onChange={(e) => setFilters({ ...filters, realEstateUsageId: e.target.value })}
                >
                  <option value="">{t('property.select.filter.all')}</option>
                  {(dropdowns.realEstateUsages || []).map(usage => (
                    <option key={usage.id || usage.Id} value={usage.id || usage.Id}>
                      {usage.nameEng || usage.NameEng}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-item">
                <label className="form-label">
                  <i className="fas fa-building"></i> {t('property.select.filter.propertyType')}
                </label>
                <select
                  className="form-select filter-select"
                  value={filters.realEstateTypeId}
                  onChange={(e) => setFilters({ ...filters, realEstateTypeId: e.target.value })}
                >
                  <option value="">{t('property.select.filter.all')}</option>
                  {(dropdowns.realEstateTypes || []).map(type => (
                    <option key={type.id || type.Id} value={type.id || type.Id}>
                      {type.nameEng || type.NameEng}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-item">
                <label className="form-label">
                  <i className="fas fa-file-alt"></i> {t('property.select.filter.ownershipDocumentType')}
                </label>
                <select
                  className="form-select filter-select"
                  value={filters.ownerDocumentType}
                  onChange={(e) => setFilters({ ...filters, ownerDocumentType: e.target.value })}
                >
                  <option value="">{t('property.select.filter.all')}</option>
                  <option value="1">{t('property.select.filter.documentTypes.titleDeed')}</option>
                  <option value="2">{t('property.select.filter.documentTypes.leaseAgreement')}</option>
                </select>
              </div>
              <div className="filter-item">
                <label className="form-label">
                  <i className="fas fa-search"></i> {t('property.select.filter.propertyName')}
                </label>
                <input
                  type="text"
                  className="form-control filter-input"
                  placeholder={t('property.select.filter.searchPlaceholder')}
                  value={filters.propertyName}
                  onChange={(e) => setFilters({ ...filters, propertyName: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleFilter()}
                />
              </div>
              <div className="filter-buttons">
                <button type="button" className="btn btn-filter-primary" onClick={handleFilter}>
                  <i className="fas fa-filter"></i> {t('property.select.filter.filterButton')}
                </button>
                <button type="button" className="btn btn-filter-secondary" onClick={handleClearFilter}>
                  <i className="fas fa-times"></i> {t('property.select.filter.clearButton')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Properties List */}
        <div className="card">
          <div className="card-header">
            <h5>{t('property.select.list.title')}</h5>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{t('property.select.list.loading')}</span>
                </div>
                <p className="mt-3 text-muted">{t('property.select.list.loading')}</p>
              </div>
            ) : properties.length === 0 ? (
              <div className="alert alert-info">
                <i className="fas fa-info-circle"></i> {t('property.select.list.noProperties')}
              </div>
            ) : (
              <>
                {totalCount > 0 && (
                  <div className="results-count">
                    <span>{totalCount} {totalCount === 1 ? t('property.select.list.title').toLowerCase().replace('list', '') : t('property.select.list.title').toLowerCase().replace('list', '')}</span>
                  </div>
                )}
                <div className="row g-3">
                  {properties.map((property) => {
                    const propId = property.id || property.Id;
                    const isSelected = selectedPropertyId === propId;
                    return (
                      <div key={propId} className="col-12 mb-3" style={{backgroundColor: 'white'}}>
                        <div
                          className={`card property-select-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleSelectProperty(propId)}
                        >
                          <div className="card-body property-card-body">
                            <div className="property-card-header">
                              <div className="property-card-title">
                                    <h5 className="mb-1">
                                  <span className="property-code">{property.code || property.Code || 'N/A'}</span>
                                  <span className="property-name">{property.nameEng || property.NameEng || property.nameAr || property.NameAr || 'N/A'}</span>
                                    </h5>
                                    {(property.nameAr || property.NameAr) && (property.nameAr || property.NameAr) !== (property.nameEng || property.NameEng) && (
                                  <p className="property-name-ar">{property.nameAr || property.NameAr}</p>
                                    )}
                                  </div>
                                </div>
                            <div className="property-details-row">
                              <div className="property-detail-badge">
                                <i className="fas fa-building"></i>
                                <span className="detail-label">{t('property.select.list.type')}</span>
                                <span className="detail-value">{property.realEstateType?.nameEng || property.RealEstateType?.NameEng || property.realEstateType?.NameEng || property.RealEstateType?.nameEng || '-'}</span>
                                  </div>
                              <div className="property-detail-badge">
                                <i className="fas fa-home"></i>
                                <span className="detail-label">{t('property.select.list.usage')}</span>
                                <span className="detail-value">{property.realEstateUsage?.nameEng || property.RealEstateUsage?.NameEng || property.realEstateUsage?.NameEng || property.RealEstateUsage?.nameEng || '-'}</span>
                                  </div>
                              <div className="property-detail-badge">
                                <i className="fas fa-city"></i>
                                <span className="detail-label">{t('property.select.list.city')}</span>
                                <span className="detail-value">{property.city?.cityNameEng || property.City?.CityNameEng || property.city?.CityNameEng || property.City?.cityNameEng || '-'}</span>
                                  </div>
                              <div className="property-detail-badge">
                                <i className="fas fa-map-marker-alt"></i>
                                <span className="detail-label">{t('property.select.list.area')}</span>
                                <span className="detail-value">{property.area?.areaNameEng || property.Area?.AreaNameEng || property.area?.AreaNameEng || property.Area?.areaNameEng || '-'}</span>
                                  </div>
                              {(property.district || property.District) && (
                                <div className="property-detail-badge">
                                  <i className="fas fa-map-pin"></i>
                                  <span className="detail-label">{t('property.select.list.district') || 'District'}</span>
                                  <span className="detail-value">{property.district?.districtNameEng || property.District?.DistrictNameEng || property.district?.DistrictNameEng || property.District?.districtNameEng || '-'}</span>
                                </div>
                              )}
                              {(property.propertyUnitsCount !== undefined || property.PropertyUnitsCount !== undefined || property.propertyUnits?.length || property.PropertyUnits?.length) && (
                                <div className="property-detail-badge units-badge">
                                  <i className="fas fa-door-open"></i>
                                  <span className="detail-label">{t('property.select.list.units') || 'Units'}</span>
                                  <span className="detail-value">{property.propertyUnitsCount || property.PropertyUnitsCount || property.propertyUnits?.length || property.PropertyUnits?.length || 0}</span>
                              </div>
                              )}
                            </div>
                            {(property.streetName || property.StreetName || property.buildingNumber || property.BuildingNumber) && (
                              <div className="property-address">
                                <i className="fas fa-map"></i>
                                <span>
                                  {[
                                    property.buildingNumber || property.BuildingNumber,
                                    property.streetName || property.StreetName,
                                    property.postalCode || property.PostalCode
                                  ].filter(Boolean).join(', ')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <div className="pagination-container mt-4">
                    <ul className="pagination">
                      <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>
                          {t('common.previous')}
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, i) => (
                        <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setPage(i + 1)}>
                            {i + 1}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                          {t('common.next')}
                        </button>
                      </li>
                    </ul>
                  </div>
                )}

                {selectedPropertyId && (
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      className="btn btn-primary-custom btn-lg"
                      onClick={handleConfirm}
                    >
                      {t('property.select.list.confirmSelection')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertySelect;

